// Author-supplied captions — a field's `label`, `description`, and `help_text`.
//
// Legacy Alpaca schemas put HTML in a field's `title`, which the importer maps
// straight to `label`; the legacy Alpaca renderer displayed it as HTML, so
// rendering it as a text node shows the tags to the user. Opt-in per field via
// `config.html_labels` (set by the importer when it detects markup), so every
// form that never carried markup renders exactly as before.
//
// `label` has no server-side sanitization — it is a plain string attribute that
// reaches the renderer as raw JSON props — so this module is the only
// sanitization layer for it.

import type { DynamicFormField } from './types'
import { labelToText, sanitizeLabelHtml } from './richText'

/** Does this field's author-supplied captions carry HTML? */
export function htmlCaptions(field: DynamicFormField): boolean {
  return field.config?.html_labels === true
}

/**
 * A caption in a markup context: sanitized HTML when the field opted in, plain
 * text otherwise. The wrapper `<span>` is deliberate —
 * `dangerouslySetInnerHTML` cannot coexist with sibling children, and the
 * label also holds the required indicator and the host's `labelSuffix`.
 */
export function Caption({ field, text }: { field: DynamicFormField; text: string }) {
  if (!htmlCaptions(field)) return <>{text}</>
  return <span dangerouslySetInnerHTML={{ __html: sanitizeLabelHtml(text) }} />
}

/** A caption in a plain-text context: aria-labels, button text, messages. */
export function captionText(field: DynamicFormField, text: string): string {
  return htmlCaptions(field) ? labelToText(text) : text
}

/**
 * An option label in a markup context. Unlike field captions this is not
 * opt-in: legacy option labels carry markup with no field-level signal, and
 * sanitized plain text renders identically.
 */
export function OptionLabel({ label }: { label: string }) {
  return <span dangerouslySetInnerHTML={{ __html: sanitizeLabelHtml(label) }} />
}
